import { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_VARIANTS } from '../../constants/button-variants';
import translation from '../../languages';
import { getFavorite } from '../../reducers/favorite';
import useUnsubscribeSchool from '../../services/school-wishlist/use-unsubscribe-school';
import SharedModal from '../../ui/modal/shared-modal';
import WarningIcon from '../../ui/font-awesome-icon/svgs/warning-triangle.red.svg';
import './remove-school-suggestion-modal.scss';

type RemoveSchoolSuggestionModalProps = TranslationProps & {
    isOpen: boolean;
    materialId: number;
    searchRequestId?: string;
    onClose: () => void;
    onRemoved?: () => void;
};

const RemoveSchoolSuggestionModal = ({
    isOpen,
    materialId,
    searchRequestId,
    onClose,
    onRemoved,
    t
}: RemoveSchoolSuggestionModalProps): ReactElement | null => {
    const dispatch = useDispatch();
    const schoolId =
        useSelector((state: RootState) => state.main.user.profileDetails.details.schoolAccount?.school_id) ?? null;

    const unsubscribeMutation = useUnsubscribeSchool(schoolId, searchRequestId);

    if (!isOpen) {
        return null;
    }

    const handleConfirm = async () => {
        try {
            await unsubscribeMutation.mutateAsync({ materialId });
            dispatch(getFavorite());
            onRemoved?.();
            onClose();
        } catch {}
    };

    const handleCancel = () => {
        if (unsubscribeMutation.isPending) {
            return;
        }

        onClose();
    };

    return (
        <SharedModal
            title={<img src={WarningIcon} alt='' className='warning-icon' />}
            onClose={handleCancel}
            dialogClassName='remove-school-suggestion-modal'
            body={
                <div className='remove-modal-content'>
                    <div className='remove-modal-title'>{t('common.schoolWishlist.removeSuggestionModal.title')}</div>
                    <div className='remove-modal-subtitle'>{t('common.schoolWishlist.removeSuggestionModal.body')}</div>
                    {unsubscribeMutation.isError && (
                        <div className='remove-modal-error'>
                            {t('common.schoolWishlist.removeSuggestionModal.errorMessage')}
                        </div>
                    )}
                </div>
            }
            btnConfig={[
                {
                    action: handleCancel,
                    className: BUTTON_VARIANTS.SECONDARY,
                    text: t('common.schoolWishlist.removeSuggestionModal.cancel'),
                    disabled: unsubscribeMutation.isPending
                },
                {
                    action: handleConfirm,
                    className: BUTTON_VARIANTS.YELLOW,
                    text: t('common.schoolWishlist.removeSuggestionModal.confirm'),
                    disabled: unsubscribeMutation.isPending
                }
            ]}
        />
    );
};

const translate = translation('common');

export default translate(RemoveSchoolSuggestionModal);
